import { getters as rights } from '@/state/modules/system';
import * as groups from './groups';
import * as types from './route-types';

// Lazy-loads view components, but with better UX. A loading view
// will be used if the component takes a while to load, falling
// back to a timeout view in case the page fails to load. You can
// use this component to lazy-load a route with:
//
// component: () => lazyLoadView(import('@views/my-view'))
//
// NOTE: Components loaded with this strategy DO NOT have access
// to in-component guards, such as beforeRouteEnter,
// beforeRouteUpdate, and beforeRouteLeave. You must either use
// route-level guards instead or lazy-load the component directly:
//
// component: () => import('@views/my-view')
//

function lazyLoadView(AsyncView) {
  const AsyncHandler = () => ({
    component: AsyncView,
    // A component to use while the component is loading.
    // eslint-disable-next-line global-require
    loading: require('@/views/Loading').default,
    // Delay before showing the loading component.
    // Default: 200 (milliseconds).
    delay: 200,
    // A fallback component in case the timeout is exceeded
    // when loading the component.
    // eslint-disable-next-line global-require
    error: require('@/views/Error').default,
    // Time before giving up trying to load the component.
    // Default: Infinity (milliseconds).
    timeout: 30000,
  });

  return Promise.resolve({
    functional: true,
    render(h, { data, children }) {
      // Transparently pass any props or children
      // to the view component.
      return h(AsyncHandler, data, children);
    },
  });
}

export default [
  {
    path: '/home',
    name: types.HOME,
    component: () => lazyLoadView(import('@/views/Home')),
  },

  {
    path: '/transactions',
    name: types.PAYMENT_TRANSACTIONS,
    component: () => lazyLoadView(import('@/views/PaymentTransactions')),
    meta: {
      isAuthRequired: true,
      isContractRequired: true,
      refreshOnLanguageChange: true,
      rights: [rights.hasAccessPaymentTransactions],
      navigation: true,
      parent: groups.CHARGING,
      banner: 'payment-transactions.jpg',
    },

    children: [
      {
        path: 'from/:from?/to/:to?',
        name: types.PAYMENT_TRANSACTIONS_OVERVIEW,
        meta: {
          autoScroll: false,
          parent: groups.CHARGING,
          banner: 'payment-transactions.jpg',
        },
        children: [
          {
            path: 'transaction/:transactionId/:tab?',
            name: types.PAYMENT_TRANSACTIONS_OVERVIEW_DETAILS,
            meta: {
              parent: types.PAYMENT_TRANSACTIONS_OVERVIEW,
              scrollToId: true,
            },
          },
        ],
      },
    ],
  },

  {
    path: '/paymentgeneration',
    component: () => lazyLoadView(import('@/views/PaymentGeneration')),
    meta: {
      isAuthRequired: true,
      isContractRequired: true,
      rights: [rights.hasAccessPayment],
    },
    children: [
      {
        path: '',
        component: () => lazyLoadView(import('@/views/PaymentGeneration/ProductSelection')),
        name: types.PAYMENT_GENERATION,
        meta: {
          navigation: true,
          parent: groups.CHARGING,
          banner: 'barring.jpg',
        },
      },
      {
        path: 'data',
        component: () => lazyLoadView(import('@/views/PaymentGeneration/ProductData')),
        name: types.PAYMENT_GENERATION_DATA,
        meta: {
          parent: types.PAYMENT_GENERATION,
        },
      },
      {
        path: 'summary',
        component: () => lazyLoadView(import('@/views/PaymentGeneration/ProductSummary')),
        name: types.PAYMENT_GENERATION_SUMMARY,
        meta: {
          parent: types.PAYMENT_GENERATION,
        },
      },
      {
        path: 'confirmation',
        component: () => lazyLoadView(import('@/views/PaymentGeneration/ProductConfirmation')),
        name: types.PAYMENT_GENERATION_CONFIRMATION,
        meta: {
          parent: types.PAYMENT_GENERATION,
        },
      },
    ],
  },

  {
    path: '/barring/factoring/profile',
    component: () => lazyLoadView(import('@/views/Barring/Factoring')),
    meta: {
      isAuthRequired: true,
      isContractRequired: true,
      rights: [rights.hasAccessBarringFactoringProfile],
    },

    children: [
      {
        path: '',
        name: types.BARRING_FACTORING_STATUS,
        component: () => lazyLoadView(import('@/views/Barring/Factoring/Status')),
        props: true,
        meta: {
          navigation: true,
          parent: groups.BARRING,
          banner: 'barring.jpg',
        },
      },
      {
        path: 'edit',
        component: () => import('@/views/Barring/Factoring/Edit'), // no lazyLoadView because we need beforeRouteLeave!
        props: true,
        children: [
          {
            path: '',
            name: types.BARRING_FACTORING_EDIT,
            component: () => lazyLoadView(import('@/views/Barring/Factoring/Edit/Partners')),
            props: true,
            meta: {
              parent: types.BARRING_FACTORING_STATUS,
            },
          },
          {
            path: 'exceptions/:id',
            name: types.BARRING_FACTORING_EDIT_EXCEPTIONS,
            component: () => lazyLoadView(import('@/views/Barring/Factoring/Edit/Exceptions')),
            props: true,
            meta: {
              parent: types.BARRING_FACTORING_EDIT,
            },
          },
        ],
      },
      {
        path: 'history',
        name: types.BARRING_FACTORING_HISTORY,
        component: () => lazyLoadView(import('@/views/Barring/Factoring/History')),
        meta: {
          refreshOnLanguageChange: true,
          parent: types.BARRING_FACTORING_STATUS,
          banner: 'barring.jpg',
          rights: [rights.isBarringShowHistoryAllowed],
        },
        children: [
          {
            path: ':selectedIndex',
            name: types.BARRING_FACTORING_HISTORY_STATUS,
            meta: {
              parent: types.BARRING_FACTORING_STATUS,
              scrollToId: true,
            },
          },
        ],
      },
      {
        path: 'history/show',
        name: types.BARRING_FACTORING_HISTORY_SHOW,
        component: () => lazyLoadView(import('@/views/Barring/Factoring/History/Show')),
        props: true,
        meta: {
          refreshOnLanguageChange: true,
          parent: types.BARRING_FACTORING_HISTORY,
        },
      },
      {
        path: 'history/show/exceptions/:id',
        name: types.BARRING_FACTORING_HISTORY_SHOW_EXCEPTIONS,
        component: () => lazyLoadView(import('@/views/Barring/Factoring/History/Show/Exceptions')),
        props: true,
        meta: {
          refreshOnLanguageChange: true,
          parent: types.BARRING_FACTORING_HISTORY_SHOW,
        },
      },
    ],
  },

  {
    path: '/barring/reselling/profile',
    component: () => lazyLoadView(import('@/views/Barring/Reselling')),
    meta: {
      isAuthRequired: true,
      isContractRequired: true,
      rights: [rights.hasAccessBarringResellingProfile],
    },

    children: [
      {
        path: '',
        name: types.BARRING_RESELLING_STATUS,
        component: () => lazyLoadView(import('@/views/Barring/Reselling/Status')),
        props: true,
        meta: {
          navigation: true,
          parent: groups.BARRING,
          banner: 'barring.jpg',
        },
      },
      {
        path: 'edit',
        name: types.BARRING_RESELLING_EDIT,
        component: () => lazyLoadView(import('@/views/Barring/Reselling/Edit')),
        meta: {
          parent: types.BARRING_RESELLING_STATUS,
        },
      },
      {
        path: 'history',
        name: types.BARRING_RESELLING_HISTORY,
        component: () => lazyLoadView(import('@/views/Barring/Reselling/History')),
        meta: {
          refreshOnLanguageChange: true,
          parent: types.BARRING_RESELLING_STATUS,
          banner: 'barring.jpg',
          rights: [rights.isBarringShowHistoryAllowed],
        },
        children: [
          {
            path: ':selectedIndex',
            name: types.BARRING_RESELLING_HISTORY_STATUS,
            meta: {
              parent: types.BARRING_RESELLING_STATUS,
              scrollToId: true,
            },
          },
        ],
      },
    ],
  },

  {
    path: '/barring/pinprotection',
    name: types.BARRING_PIN_PROTECTION,
    component: () => lazyLoadView(import('@/views/Barring/PinProtection')),
    meta: {
      isAuthRequired: true,
      isContractRequired: true,
      refreshOnLanguageChange: true,
      rights: [rights.hasAccessBarringPinProtection],
      navigation: true,
      parent: groups.BARRING,
      banner: 'pin-protection.jpg',
    },
  },

  { path: '/unbarring/its*', redirect: '/unbarring' },
  {
    path: '/unbarring',
    component: () => import('@/views/Unbarring'),
    meta: {
      layout: () => import('@/layouts/Minimal'),
      isUnbarringPage: true,
    },

    children: [
      {
        path: '',
        name: types.UNBARRING,
        component: () => import('@/views/Unbarring/Access'),
        meta: {
          banner: 'unbarring.jpg',
        },
      },
      {
        path: 'otp',
        name: types.UNBARRING_OTP,
        component: () => import('@/views/Unbarring/Confirmation'),
        meta: {
          banner: 'unbarring.jpg',
        },
      },
      {
        path: 'pin',
        name: types.UNBARRING_PIN,
        component: () => import('@/views/Unbarring/Pin'),
        meta: {
          banner: 'unbarring.jpg',
        },
      },
    ],
  },

  {
    path: '/recharge/transactions',
    name: types.RECHARGE_TRANSACTIONS,
    component: () => lazyLoadView(import('@/views/RechargeTransactions')),
    meta: {
      isAuthRequired: true,
      isContractRequired: true,
      refreshOnLanguageChange: true,
      rights: [rights.hasAccessRechargeTransactions],
      navigation: true,
      parent: groups.RECHARGING,
      banner: 'recharge-transactions.jpg',
    },

    children: [
      {
        path: 'from/:from?/to/:to?/autoTopupId/:autoTopupId?',
        name: types.RECHARGE_TRANSACTIONS_OVERVIEW,
        meta: {
          autoScroll: false,
          parent: groups.RECHARGING,
          banner: 'recharge-transactions.jpg',
        },
        children: [
          {
            path: 'transaction/:transactionId/:transactionIds?',
            name: types.RECHARGE_TRANSACTIONS_OVERVIEW_DETAILS,
            meta: {
              parent: types.RECHARGE_TRANSACTIONS_OVERVIEW,
              scrollToId: true,
              rights: [rights.hasShowCCTransactionView],
            },
          },
        ],
      },
    ],
  },

  {
    path: '/catalogue',
    component: () => lazyLoadView(import('@/views/Substore/Product/Container')),
    props: true,
    meta: {
      isAuthRequired: true,
      isContractRequired: true,
      refreshOnLanguageChange: true,
    },

    children: [
      {
        path: '',
        name: types.PRODUCT_CATALOGUE,
        component: () => lazyLoadView(import('@/views/Substore/Product/Catalogue')),
        meta: {
          rights: [rights.hasAccessCatalogue],
          navigation: true,
          parent: groups.PRODUCTS,
          banner: 'catalogue.jpg',
        },
      },
      {
        path: ':originalSubscriptionTemplateId?/:subscriptionTemplateId/details',
        name: types.PRODUCT_DETAILS,
        component: () => lazyLoadView(import('@/views/Substore/Product/Details')),
        props: true,
        meta: {
          parent: types.PRODUCT_CATALOGUE,
        },
      },
      {
        path: ':originalSubscriptionTemplateId?/:subscriptionTemplateId/selection',
        name: types.PRODUCT_ORDER_SELECTION,
        component: () => lazyLoadView(import('@/views/Substore/Product/OrderSelection')),
        props: true,
        meta: {
          parent: types.PRODUCT_DETAILS,
        },
      },
      {
        path: ':subscriptionTemplateId/selection/finalize',
        name: types.PRODUCT_ORDER_FINALIZE,
        component: () => lazyLoadView(import('@/views/Substore/Product/OrderSelection')),
        props: true,
        meta: {
          parent: types.STOCK,
        },
      },
      {
        path: ':subscriptionTemplateId/account/finalize',
        name: types.PRODUCT_ORDER_ACCOUNT_FINALIZE,
        component: () => lazyLoadView(import('@/views/Substore/Product/OrderAccount')),
        props: true,
        meta: {
          parent: types.STOCK,
        },
      },
      {
        path: 'return',
        name: types.PRODUCT_ORDER_SUMMARY_RETURN,
        component: () => lazyLoadView(import('@/views/Substore/Product/OrderSummary')),
        meta: {
          parent: types.PRODUCT_DETAILS,
        },
      },
      {
        path: 'openid/return',
        name: types.PRODUCT_ORDER_SUMMARY_OPENID_RETURN,
        component: () => lazyLoadView(import('@/views/Substore/Product/OrderSummary')),
        meta: {
          parent: types.PRODUCT_DETAILS,
        },
      },
      {
        path: ':originalSubscriptionTemplateId?/:subscriptionTemplateId/summary/:orderCallId',
        name: types.PRODUCT_ORDER_SUMMARY,
        component: () => lazyLoadView(import('@/views/Substore/Product/OrderSummary')),
        props: true,
        meta: {
          parent: types.PRODUCT_DETAILS,
        },
      },
      {
        path: ':originalSubscriptionTemplateId?/:subscriptionTemplateId/summary/:orderCallId/finalize',
        name: types.PRODUCT_ORDER_SUMMARY_FINALIZE,
        component: () => lazyLoadView(import('@/views/Substore/Product/OrderSummary')),
        props: true,
        meta: {
          parent: types.STOCK,
        },
      },
      {
        path: ':originalSubscriptionTemplateId?/:subscriptionTemplateId/reservationconfirmation',
        name: types.PRODUCT_ORDER_RESERVATION_CONFIRMATION,
        component: () => lazyLoadView(import('@/views/Substore/Product/ReservationConfirmation')),
        props: true,
        meta: {
          parent: types.PRODUCT_DETAILS,
        },
      },
      {
        path: ':originalSubscriptionTemplateId?/:subscriptionTemplateId/p/:priceEncoded?/confirmation/:confirmationInProgress?',
        name: types.PRODUCT_ORDER_CONFIRMATION,
        component: () => lazyLoadView(import('@/views/Substore/Product/OrderConfirmation')),
        props: true,
        meta: {
          parent: types.PRODUCT_DETAILS,
        },
      },
      {
        path: ':subscriptionTemplateId/confirmation/finalize',
        name: types.PRODUCT_ORDER_CONFIRMATION_FINALIZE,
        component: () => lazyLoadView(import('@/views/Substore/Product/OrderConfirmation')),
        props: true,
        meta: {
          parent: types.STOCK,
        },
      },
    ],
  },

  {
    path: '/stock',
    component: () => lazyLoadView(import('@/views/Substore/Stock/Container')),
    props: true,
    meta: {
      isAuthRequired: true,
      refreshOnLanguageChange: true,
      rights: [rights.hasAccessStockOverview],
    },

    children: [
      {
        path: '',
        name: types.STOCK,
        component: () => lazyLoadView(import('@/views/Substore/Stock/Overview')),
        props: true,
        meta: {
          navigation: true,
          parent: groups.PRODUCTS,
          banner: 'stock.jpg',
        },
      },
      {
        path: ':subscriptionTemplateId/:uniqueSubscriptionId/change/selection',
        name: types.STOCK_PRODUCT_CHANGE_SELECTION,
        component: () => lazyLoadView(import('@/views/Substore/Stock/ProductChangeSelection')),
        props: true,
        meta: {
          parent: types.STOCK,
          banner: 'catalogue.jpg',
        },
      },
      {
        path: ':subscriptionTemplateId/:uniqueSubscriptionId/:newSubscriptionTemplateId/change/summary',
        name: types.STOCK_PRODUCT_CHANGE_SUMMARY,
        component: () => lazyLoadView(import('@/views/Substore/Stock/ChangeSummary')),
        props: true,
        meta: {
          parent: types.STOCK,
          banner: 'catalogue.jpg',
        },
      },
      {
        path: ':newSubscriptionTemplateId/:newUniqueSubscriptionId/change/confirmation/:confirmationInProgress?',
        name: types.STOCK_PRODUCT_CHANGE_CONFIRMATION,
        component: () => lazyLoadView(import('@/views/Substore/Stock/ChangeConfirmation')),
        props: true,
        meta: {
          parent: types.STOCK,
          banner: 'catalogue.jpg',
        },
      },
      {
        path: ':subscriptionTemplateId/optin/summary/:orderCallId',
        name: types.PRODUCT_ORDER_SUMMARY_OPTIN,
        component: () => lazyLoadView(import('@/views/Substore/Product/OrderSummary')),
        props: true,
        meta: {
          parent: types.STOCK,
          banner: 'catalogue.jpg',
        },
      },
      {
        path: ':subscriptionTemplateId/optin/confirmation/:confirmationInProgress?',
        name: types.PRODUCT_ORDER_CONFIRMATION_OPTIN,
        component: () => lazyLoadView(import('@/views/Substore/Product/OrderConfirmation')),
        props: true,
        meta: {
          parent: types.STOCK,
          banner: 'catalogue.jpg',
        },
      },
      {
        path: ':subscriptionTemplateId/:uniqueSubscriptionId/administration/selection',
        name: types.PRODUCT_ADMINISTRATION_SELECTION,
        component: () => lazyLoadView(import('@/views/Substore/Stock/Administration/AdministrationSelection')),
        props: true,
        meta: {
          parent: types.STOCK,
          banner: 'catalogue.jpg',
        },
      },
      {
        path: ':subscriptionTemplateId/:uniqueSubscriptionId/administration/summary',
        name: types.PRODUCT_ADMINISTRATION_SUMMARY,
        component: () => lazyLoadView(import('@/views/Substore/Stock/Administration/AdministrationSummary')),
        props: true,
        meta: {
          parent: types.STOCK,
          banner: 'catalogue.jpg',
        },
      },
      {
        path: ':subscriptionTemplateId/:uniqueSubscriptionId/administration/confirmation',
        name: types.PRODUCT_ADMINISTRATION_CONFIRMATION,
        component: () => lazyLoadView(import('@/views/Substore/Stock/Administration/AdministrationConfirmation')),
        props: true,
        meta: {
          parent: types.STOCK,
          banner: 'catalogue.jpg',
        },
      },
      {
        path: ':subscriptionTemplateId/:uniqueSubscriptionId/administration/confirmationInProgress',
        name: types.PRODUCT_ADMINISTRATION_CONFIRMATION_IN_PROGRESS,
        component: () => lazyLoadView(import('@/views/Substore/Stock/Administration/AdministrationConfirmationInProgress')),
        props: true,
        meta: {
          parent: types.STOCK,
          banner: 'catalogue.jpg',
        },
      },
    ],
  },

  {
    path: '/orderoverview',
    name: types.ORDER_OVERVIEW,
    component: () => lazyLoadView(import('@/views/OrderOverview')),
    meta: {
      isAuthRequired: true,
      rights: [rights.hasAccessOrderOverview],
      navigation: true,
      parent: groups.PRODUCTS,
      banner: 'order.jpg',
    },
  },

  {
    path: '/internalcatalogue',
    name: types.INTERNAL_CATALOGUE,
    component: () => lazyLoadView(import('@/views/InternalCatalogue')),
    meta: {
      isAuthRequired: true,
      isContractRequired: true,
      rights: [rights.hasAccessInternalCatalogue],
      navigation: true,
      parent: groups.PRODUCTS,
      banner: 'catalogue.jpg',
    },
  },

  {
    path: '/internalstock',
    name: types.INTERNAL_STOCK,
    component: () => lazyLoadView(import('@/views/InternalStock')),
    meta: {
      isAuthRequired: true,
      isContractRequired: true,
      rights: [rights.hasAccessInternalStock],
      navigation: true,
      parent: groups.PRODUCTS,
      banner: 'stock.jpg',
    },
  },

  {
    path: '/autotopupregistrations',
    component: () => lazyLoadView(import('@/views/AutoTopUpRegistrations')),
    name: types.AUTO_TOPUP_REGISTRATIONS,
    meta: {
      isAuthRequired: true,
      isContractRequired: true,
      rights: [rights.hasAccessAutoTopUpRegistrations],
      navigation: true,
      refreshOnLanguageChange: true,
      parent: groups.RECHARGING,
      banner: 'recharge-transactions.jpg',
    },
    children: [
      {
        path: 'id/:autoTopUpId',
        name: types.AUTO_TOPUP_REGISTRATIONS_OVERVIEW_DETAILS,
        meta: {
          scrollToId: true,
          parent: types.AUTO_TOPUP_REGISTRATIONS,
        },
      },
    ],
  },

  { path: '/auth/its*', redirect: '/auth' },
  {
    path: '/auth',
    component: () => import('@/views/Auth'),
    meta: {
      layout: () => import('@/layouts/Minimal'),
      isAuthPage: true,
    },

    children: [
      {
        path: '',
        name: types.AUTH,
        component: () => import('@/views/Auth/Identification'),
        meta: {
          banner: 'payment-transactions.jpg',
          bannerLocaleOverride: 'auth.backgroundImage',
        },
      },

      {
        path: 'confirmation',
        name: types.AUTH_CONFIRMATION,
        component: () => import('@/views/Auth/Confirmation'),
        meta: {
          banner: 'payment-transactions.jpg',
          bannerLocaleOverride: 'auth.backgroundImage',
        },
      },
    ],
  },

  { path: '/cita/its*', redirect: '/cita' },
  {
    path: '/cita/help',
    name: types.CITA_HELP,
    component: () => import('@/views/Cita/Help'),
    meta: {
      layout: () => import('@/layouts/Cita'),
      isCitaPage: true,
      banner: 'payment-transactions.jpg',
      bannerLocaleOverride: 'cita.backgroundImage',
    },
  },
  {
    path: '/cita',
    name: types.CITA,
    component: () => import('@/views/Cita'),
    meta: {
      layout: () => import('@/layouts/Cita'),
      isCitaPage: true,
    },

    children: [
      {
        path: 'identify',
        name: types.CITA_IDENTIFY,
        component: () => import('@/views/Cita/Identify'),
        meta: {
          banner: 'payment-transactions.jpg',
          bannerLocaleOverride: 'cita.backgroundImage',
        },
      },
      {
        path: 'auth',
        name: types.CITA_AUTH,
        component: () => import('@/views/Cita/Auth'),
        meta: {
          banner: 'payment-transactions.jpg',
          bannerLocaleOverride: 'cita.backgroundImage',
        },
      },
      {
        path: 'request',
        name: types.CITA_REQUEST,
        component: () => import('@/views/Cita/Request'),
        meta: {
          banner: 'payment-transactions.jpg',
          bannerLocaleOverride: 'cita.backgroundImage',
        },
      },
      {
        path: 'confirm',
        name: types.CITA_CONFIRM,
        component: () => import('@/views/Cita/Confirm'),
        meta: {
          banner: 'payment-transactions.jpg',
          bannerLocaleOverride: 'cita.backgroundImage',
        },
      },
    ],
  },

  {
    path: '/bundles',
    name: types.CONTENT_BUNDLES,
    component: () => lazyLoadView(import('@/views/ContentBundles')),
    meta: {
      isAuthRequired: true,
      isContractRequired: true,
      rights: [rights.hasAccessContentBundles],
      navigation: true,
      parent: groups.CHARGING,
      banner: 'content-bundles.jpg',
    },

    children: [
      {
        path: 'from/:from/to/:to',
        name: types.CONTENT_BUNDLES_OVERVIEW,
        meta: {
          autoScroll: false,
          parent: groups.CHARGING,
          refreshOnLanguageChange: true,
          banner: 'content-bundles.jpg',
        },
        children: [
          {
            path: 'instance/:instanceId',
            name: types.CONTENT_BUNDLES_OVERVIEW_DETAILS,
            meta: {
              parent: types.CONTENT_BUNDLES_OVERVIEW,
              scrollToId: true,
            },
          },
        ],
      },
    ],
  },

  {
    path: '/confirmations',
    name: types.PERSISTENT_CONFIRMATIONS,
    component: () => lazyLoadView(import('@/views/PersistentConfirmations')),
    props: (route) => ({ id: route.params.id === undefined ? null : Number(route.params.id) }),
    meta: {
      isAuthRequired: true,
      isContractRequired: true,
      refreshOnLanguageChange: true,
      rights: [rights.hasAccessPersistentConfirmations],
      navigation: true,
      parent: groups.CHARGING,
      banner: 'content-bundles.jpg',
    },
    children: [
      {
        path: 'id/:id/:tab?',
        name: types.PERSISTENT_CONFIRMATIONS_DETAILS,
        meta: {
          scrollToId: true,
        },
      },
    ],
  },

  {
    path: '/customer',
    name: types.CUSTOMER_SEARCH,
    component: () => lazyLoadView(import('@/views/CustomerSearch')),
    meta: {
      isAuthRequired: true,
      rights: [rights.isCustomerSearchAllowed],
      banner: 'customer-contract.jpg',
    },
  },

  {
    path: '/customer/redirect',
    name: types.CUSTOMER_SEARCH_REDIRECT,
    component: () => lazyLoadView(import('@/views/CustomerSearch')),
    props: true,
    meta: {
      isAuthRequired: true,
      rights: [rights.isCustomerSearchAllowed],
      banner: 'customer-contract.jpg',
    },
  },

  {
    path: '/contract',
    name: types.CONTRACT_SEARCH,
    component: () => lazyLoadView(import('@/views/ContractSearch')),
    meta: {
      isAuthRequired: true,
      rights: [rights.isContractSearchAllowed],
      banner: 'customer-contract.jpg',
      refreshOnLanguageChange: true,
    },
  },

  {
    path: '/contract/redirect',
    name: types.CONTRACT_SEARCH_REDIRECT,
    component: () => lazyLoadView(import('@/views/ContractSearch')),
    props: true,
    meta: {
      isAuthRequired: true,
      rights: [rights.isContractSearchAllowed],
      banner: 'customer-contract.jpg',
      refreshOnLanguageChange: true,
    },
  },

  {
    path: '/login',
    name: types.LOGIN,
    component: () => lazyLoadView(import('@/views/Login')),
    props: true,
    meta: {
      layout: () => import('@/layouts/Minimal'),
      isNotAuthRequired: true,
    },
  },

  {
    path: '/logout',
    name: types.LOGOUT,
    component: () => import('@/views/Logout'),
    meta: {
      layout: () => import('@/layouts/Minimal'),
    },
  },

  {
    path: '/profile',
    name: types.PROFILE,
    component: () => lazyLoadView(import('@/views/Profile')),
    meta: {
      isAuthRequired: true,
    },
  },

  {
    path: '/password',
    name: types.PASSWORD,
    component: () => lazyLoadView(import('@/views/Profile/Password')),
    meta: {
      isAuthRequired: true,
      banner: 'pin-protection.jpg',
    },
  },

  {
    path: '/error/partner/:partner?/:errorCode',
    name: types.ERROR_PARTNER_ADAPTER,
    component: () => import('@/views/Error/PartnerAdapter'),
    props: true,
    meta: {
      isErrorPage: true,
      hideBreadcrumb: true,
      layout: () => import('@/layouts/Minimal'),
    },
  },

  {
    path: '/error',
    name: types.ERROR,
    component: () => import('@/views/Error'),
    meta: {
      isErrorPage: true,
      hideBreadcrumb: true,
    },
    props: true,

    children: [
      {
        path: 'fatal/:code/:errorLevel?',
        name: types.ERROR_FATAL_CODE,
        meta: {
          layout: () => import('@/layouts/Minimal'),
        },
      },
      {
        path: 'retry/:code/:errorLevel?',
        name: types.ERROR_RETRY_CODE,
      },
      {
        path: 'medium/:code/:errorLevel?',
        name: types.ERROR_MEDIUM_CODE,
      },
    ],
  },

  {
    path: '/test',
    name: types.TEST,
    component: () => import('@/views/Test/'),
    meta: {
      hideBreadcrumb: true,
    },

    children: [
      {
        path: 'general',
        name: types.TEST_GENERAL,
        component: () => import('@/views/Test/EmptyRouterView'),

        children: [
          {
            path: 'anonymous',
            name: types.TEST_ANONYMOUS,
            component: () => import('@/views/Test/Anonymous'),
            meta: {
              parent: types.TEST_GENERAL,
            },
          },
          {
            path: 'date',
            name: types.TEST_DATE,
            component: () => import('@/views/Test/Date'),
            meta: {
              parent: types.TEST_GENERAL,
            },
          },
          {
            path: 'tabset/:test?',
            name: types.TEST_TABSET,
            component: () => import('@/components/UI/Tabs/Test'),
            meta: {
              parent: types.TEST_GENERAL,
            },
          },
          {
            path: 'form',
            name: types.TEST_FORM,
            component: () => import('@/components/Form/Test'),
            meta: {
              parent: types.TEST_GENERAL,
            },
          },
          {
            path: 'dl',
            name: types.TEST_DESCRIPTIONLIST,
            component: () => import('@/components/DescriptionList/Test'),
            meta: {
              parent: types.TEST_GENERAL,
            },
          },
          {
            path: 'detailbox',
            name: types.TEST_DETAILBOX,
            component: () => import('@/components/UI/DetailBox/Test'),
            meta: {
              parent: types.TEST_GENERAL,
            },
          },
          {
            path: 'table',
            name: types.TEST_TABLE,
            component: () => import('@/components/Table/Test'),
            meta: {
              parent: types.TEST_GENERAL,
            },
          },
        ],
      },

      {
        path: 'substore',
        name: types.TEST_SUBSTORE,
        component: () => import('@/views/Test/EmptyRouterView'),

        children: [
          {
            path: 'price',
            name: types.TEST_PRICE,
            component: () => import('@/components/Substore/Price/Test'),
            meta: {
              parent: types.TEST_SUBSTORE,
            },
          },
          {
            path: 'pricedetails',
            name: types.TEST_PRICEDETAILS,
            component: () => import('@/components/Substore/PriceDetails/Test'),
            meta: {
              parent: types.TEST_SUBSTORE,
            },
          },
          {
            path: 'progress',
            name: types.TEST_PROGRESS,
            component: () => import('@/components/Progress/Test'),
            meta: {
              parent: types.TEST_SUBSTORE,
            },
          },
          {
            path: 'product',
            name: types.TEST_PRODUCT,
            component: () => import('@/components/Substore/Product/Test'),
            meta: {
              parent: types.TEST_SUBSTORE,
            },
          },
          {
            path: 'stockproduct',
            name: types.TEST_STOCKPRODUCT,
            component: () => import('@/components/Substore/StockProduct/Test'),
            meta: {
              parent: types.TEST_SUBSTORE,
            },
          },
          {
            path: 'subscription',
            name: types.TEST_SUBSCRIPTION,
            component: () => import('@/components/Substore/InternalCatalogue/Subscription/Test'),
            meta: {
              parent: types.TEST_SUBSTORE,
            },
          },
        ],
      },

      {
        path: 'barring',
        name: types.TEST_BARRING,
        component: () => import('@/views/Test/EmptyRouterView'),

        children: [
          {
            path: 'partnerselection',
            name: types.TEST_PARTNERSELECTION,
            component: () => import('@/components/Barring/PartnerSelection/Test'),
            meta: {
              parent: types.TEST_BARRING,
            },
          },
          {
            path: 'status',
            name: types.TEST_BARRING_STATUS,
            component: () => import('@/components/Barring/Status/Test'),
            meta: {
              parent: types.TEST_BARRING,
            },
          },
        ],
      },
    ],
  },

  {
    path: '/',
    redirect: { name: types.HOME },
  },

  {
    path: '/*',
    redirect: { name: types.HOME },
  },
];
